<template>
  <section>
    <p class="pricing__type">Promo code</p>
    <Pill v-if="showCouponAppliedPill" info class="pricing__coupon-pill-wrapper">
      COUPON APPLIED
    </Pill>
    <form @submit.prevent class="pricing__promo">
      <template v-if="couponObjStackValidity">
        <div
          v-for="couponObj in couponObjStack"
          :key="`${couponObj.code}-${couponObj.isEligibleForCart}`"
          class="pricing__coupon-wrapper"
        >
          <div
            :class="['pricing__coupon', !couponObj.isEligibleForCart && 'pricing__coupon--invalid']"
          >
            <span>{{ couponObj.couponStr }}</span>
            <div
              class="pricing__coupon-close"
              @click.prevent="removeCoupon(couponObj.couponStr)"
            ></div>
          </div>
          <div
            :class="[
              'pricing__coupon-label',
              !couponObj.isEligibleForCart && 'pricing__coupon-label--invalid',
            ]"
          >
            <vue-loaders
              v-if="isCouponLoading"
              class="pricing__promo-input-spinner"
              name="line-spin-fade-loader"
              color="#dfe1e8"
              scale=".3"
            ></vue-loaders>
            {{ couponLabel(couponObj) }}
          </div>
        </div>
      </template>
      <div
        :class="[
          'pricing__promo-input-wrapper',
          couponObjStackValidity && 'pricing__promo-input-wrapper--hide',
        ]"
      >
        <input
          class="ine-input pricing__promo-input"
          type="text"
          placeholder="Enter promo code here"
          v-model="couponStr"
        />
        <vue-loaders
          v-if="isCouponLoading"
          class="pricing__promo-input-spinner"
          name="line-spin-fade-loader"
          color="#dfe1e8"
          scale=".3"
        ></vue-loaders>
        <button
          type="submit"
          :class="[
            'btn',
            'btn--primary',
            'pricing__btn',
            couponObjStackValidity && 'pricing__btn--hide',
          ]"
          :disabled="!couponStr"
          @click="addCoupon()"
        >
          Apply
        </button>
      </div>
    </form>
  </section>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { promoProductBundleUtils } from '@/helpers/mixins'
import Pill from '@/components/shared/Pill.vue'

export default {
  name: 'PricingListPromoCode',
  mixins: [promoProductBundleUtils],
  components: {
    Pill,
  },
  data() {
    return {
      couponStr: '',
      couponSpinner: false,
      pricingDiscount: '0.00',
      isAnyCouponEligible: false,
    }
  },
  computed: {
    ...mapGetters({
      showPromotionSavings: 'priceDetails/getShowPromotionSavings',
      shoppingCart: 'shoppingCart/getShoppingCart',
      couponObjStack: 'coupons/getCouponObjStack',
      isCouponLoading: 'coupons/isCouponLoading',
    }),
    showCouponAppliedPill() {
      return this.couponObjStackValidity && this.isAnyCouponEligible
    },
    couponObjStackValidity() {
      return this.couponObjStack && this.couponObjStack.length > 0
    },
  },
  watch: {
    shoppingCart: {
      handler() {
        if (this.shoppingCart && this.shoppingCart.length > 0 && this.couponObjStackValidity) {
          this.updateCouponObjStack()
        }
        this.isAnyCouponEligibleCompute()
      },
      immediate: true,
    },
    couponObjStack() {
      this.couponStr = ''
      this.isAnyCouponEligibleCompute()
      this.priceDetailsUpdate()
    },
  },
  methods: {
    ...mapActions({
      addCouponAction: 'coupons/addCoupon',
      removeCouponAction: 'coupons/removeCoupon',
      updateCouponObjStack: 'coupons/updateCouponObjStack',
      priceDetailsUpdate: 'priceDetails/priceDetailsUpdate',
    }),
    addCoupon() {
      if (this.couponStr && this.couponStr.trim()) {
        this.couponStr = this.couponStr.trim()
        this.addCouponAction(this.couponStr)
      } else {
        this.couponStr = ''
      }
    },
    removeCoupon(couponStr) {
      this.removeCouponAction(couponStr)
      this.couponStr = ''
    },
    couponLabel(couponObj) {
      let couponLabel = null
      if (couponObj.isEligibleForCart) {
        couponLabel = `Nice! You're saving $${(couponObj.discountAmount || 0).toFixed(2)}`
      } else {
        couponLabel = this.showPromotionSavings
          ? 'This coupon cannot be applied as your purchase already includes a discount/promotion.'
          : 'Code invalid'
      }
      return couponLabel
    },
    isAnyCouponEligibleCompute() {
      if (!this.couponObjStack) {
        this.isAnyCouponEligible = false
        return
      }
      this.isAnyCouponEligible = this.couponObjStack.some(couponObj => {
        return couponObj.isEligibleForCart
      })
    },
  },
}
</script>

<style lang="scss">
.pricing {
  height: 100%;
  width: 100%;
  margin: 0 auto;
  &__type {
    font-family: var(--font-regular);
    color: var(--white);
    font-size: rem(20px);
    line-height: 26px;
    text-align: left;
    font-weight: 700;
    margin-bottom: 10px;
  }
  &__prices-wrapper {
    position: relative;
    margin: 20px 0 0 0;
    @include tablet {
      margin: 50px 0 0 0;
      margin: 0;
    }
  }
  &__promo {
    position: relative;
    font-family: var(--font-regular);
    outline: none;
    border: none;
    margin: 12.5px auto;
    padding: 0;
    width: 100%;
    height: 48px;
    display: flex;
    align-items: center;
    &-input {
      height: 46px;
      width: 100%;
      &-wrapper {
        width: 100%;
        display: flex;
        gap: 15px;
        &--hide {
          display: none;
        }
      }
      &-spinner {
        position: absolute;
        right: 100px;
        bottom: -3px;
      }
    }
  }
  &__btn {
    &--hide {
      visibility: hidden;
    }
  }
  &__coupon {
    white-space: nowrap;
    height: 26px;
    padding: 5px 7px 5px 10px;
    color: var(--white);
    gap: 10px;
    border-radius: 6px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: var(--purple);

    &-wrapper {
      display: grid;
      grid-template-columns: auto 2.5fr;
      grid-template-rows: 1fr;
      grid-column-gap: 10px;
      grid-row-gap: 0px;
      align-items: center;
    }
    &-label {
      color: var(--purple-pale);
      font-size: rem(14);
      line-height: 20px;
      margin-left: 10px;
      text-align: left;
      &--invalid {
        color: var(--performance-neg);
      }
    }
    &-close {
      cursor: pointer;
      width: 16px;
      height: 24px;
      background-image: url("data:image/svg+xml,%3Csvg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M18 6L6 18' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3Cpath d='M6 6L18 18' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E%0A");
      background-size: 18px 24px;
      background-repeat: no-repeat;
    }
    &--invalid {
      background: var(--performance-neg);
    }
  }
  &__coupon-pill-wrapper {
    position: absolute;
    top: 0;
    right: 0;
    color: var(--white);
    background: var(--purple-gradient);
  }
}
</style>
